import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { QRCodeCanvas } from 'qrcode.react'

import { createDemoTournament, DEMO_FORMAT_META } from '../lib/demos.js'
import { copyTemplateToTournament } from '../lib/templates.js'
import { buildDemoTournament } from '../utils/aiActionExecutor.js'
import { db } from '../lib/firebase.js'

// Template paths used by "Set Up Manually" — deep-copy these into a new
// /tournaments/{id} via copyTemplateToTournament (pure Firebase, no AI)
// so the director can configure flights, tee times, brackets, calcutta
// etc. manually in the tournament UI, exactly the way the demo button
// worked before the AI was added. Keyed by the demo def's `kind` field
// set in src/lib/demos.js.
const MANUAL_TEMPLATE_PATHS = {
  full:       'templates/demos/pbcc-4-ball',
  strokePlay: 'templates/demos/stroke-play-36',
}
const MANUAL_DEMO_NAMES = {
  full:       'PBCC 4-Ball Championship Demo',
  strokePlay: 'PBCC Stroke Play Demo',
}
// Plain-language one-liner shown in the "Set Up with AI" confirm step so the
// director sees exactly what the build will create before anything is written.
// Describes the saved template that COPY_TOURNAMENT pulls from (fixed 96-team
// 4-Ball / 36-player stroke play), not the 96/104 preview toggle.
const SETUP_SUMMARY = {
  full:       'PBCC 4-Ball · 96 teams · 12 flights · from qualifying list',
  strokePlay: 'PBCC Stroke Play · 36 players · 18 shotgun groups',
}

const BUTTONS = [
  {
    key: 'fourBallMatch',
    icon: '🏌️',
    label: '4-Ball Match Play Demo',
    desc: 'Match play tournament with flights, pairings and live scoring',
  },
  {
    key: 'bestBallField',
    icon: '⛳',
    label: 'Best Ball · vs Field',
    desc: 'Teams only, no qualifying — 36 two-man teams (72 players), one field; best score per hole, ranked vs the whole field.',
  },
  {
    key: 'bestBallFlight',
    icon: '⛳',
    label: 'Best Ball · Flighted',
    desc: 'Teams only, no qualifying — 36 two-man teams in flights of 8; ranked within each flight.',
  },
  {
    key: 'scramble',
    icon: '🌀',
    label: 'Scramble · vs Field',
    desc: 'Teams only, no qualifying — 18 four-man teams (72 players), one field; ranked vs the whole field.',
  },
  {
    key: 'scrambleFlight',
    icon: '🌀',
    label: 'Scramble · Flighted',
    desc: 'Teams only, no qualifying — 18 four-man teams in flights of 8; ranked within each flight.',
  },
  {
    key: 'shamble',
    icon: '🤝',
    label: 'Shamble · vs Field',
    desc: 'Teams only — best drive then own ball in; one team score/hole, ranked vs the field (strokes).',
  },
  {
    key: 'alternateShot',
    icon: '🔁',
    label: 'Alternate Shot · vs Field',
    desc: 'Teams only — 2-man, one ball alternating; one team score/hole, ranked vs the field (strokes).',
  },
  {
    key: 'stableford',
    icon: '🎯',
    label: 'Stableford · vs Field',
    desc: 'Teams only — points per hole (par 2, birdie 3, eagle 4); most points wins.',
  },
  {
    key: 'quota',
    icon: '📈',
    label: 'Quota · vs Field',
    desc: 'Teams only — points (bogey 1/par 2/birdie 4/eagle 8) vs a 36−handicap quota; most over quota wins.',
  },
  {
    key: 'skins',
    icon: '💰',
    label: 'Skins · vs Field',
    desc: 'Teams only — low score/hole wins the skin; ties carry over and stack.',
  },
  {
    key: 'strokePlay',
    icon: '📊',
    label: 'Stroke Play Demo',
    desc: '36-player individual stroke play, shotgun start, gross + net',
  },
  {
    key: 'fullTournament',
    icon: '🏆',
    label: 'Full Tournament Demo',
    desc: 'Complete 96-team 4-Ball Championship with all features',
  },
  {
    key: 'ghinHandicap',
    icon: '📇',
    label: '(GH)# / Handicap Demo',
    desc: '4-Ball · per-player indexes, (GH)# vs self-reported flags, gross + net',
  },
]

// Teams-only / no-qualifying demos that follow the team-list → Build with AI →
// Tournament Center flow. field defs are single-flight; flight defs are multi.
const TEAMS_ONLY_KEYS = ['scramble', 'scrambleFlight', 'bestBallField', 'bestBallFlight', 'shamble', 'alternateShot', 'stableford', 'quota', 'skins']

export default function DemoLauncher({ buttonClass = 'btn-secondary text-sm', onDemoLaunched }) {
  const navigate = useNavigate()
  const [open, setOpen] = useState(false)
  const [active, setActive] = useState(null) // { tournamentId, def }
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState('')
  // Full Tournament Demo roster size, chosen by tap before launch. Default 96.
  const [fullSize, setFullSize] = useState(96)

  // Reset active when launching a new demo so old QRs go away.
  const launch = async (key, size) => {
    // Teams-only / no-qualifying demos (scramble/stroke × field/flight): follow
    // the REAL program sequence — format → TEAM LIST → ✨ Build with AI →
    // Tournament Center. Do NOT build yet; the Build button in ScrambleActive
    // runs the AI build and navigates. (Other demos keep their behaviour.)
    if (TEAMS_ONLY_KEYS.includes(key)) {
      setErr('')
      setActive({ stage: 'teamlist', key, def: DEMO_FORMAT_META[key] })
      return
    }
    setBusy(true)
    setErr('')
    try {
      const result = await createDemoTournament(key, size ? { size } : {})
      setActive(result)
      // Notify parent — the dashboard uses this to flip the AI Assistant
      // into demo mode after the Full Tournament Demo is launched.
      if (typeof onDemoLaunched === 'function') {
        onDemoLaunched(key, result)
      }
    } catch (e) {
      setErr(e?.message || String(e))
    } finally {
      setBusy(false)
    }
  }

  // Teams-only BUILD step: build via the SAME AI builder as every other demo
  // (buildDemoTournament), then drop the director into the tournament center
  // (/admin/t/{id}) — TV control, leaderboard, tee times, QR Codes tab, GPS —
  // exactly where the 4-Ball demo lands. No standalone QR dead-end.
  const buildTeamsOnly = async (key) => {
    setBusy(true)
    setErr('')
    try {
      const result = await buildDemoTournament(db, { mode: key })
      if (!result?.tournamentId) {
        setErr(result?.message || 'AI build failed.')
        return
      }
      if (typeof onDemoLaunched === 'function') onDemoLaunched(key, result)
      setOpen(false)
      setActive(null)
      navigate(`/admin/t/${result.tournamentId}`)
    } catch (e) {
      setErr(e?.message || String(e))
    } finally {
      setBusy(false)
    }
  }

  // "Set Up Manually" — pure Firebase deep-copy from /templates/<path>
  // into a brand-new /tournaments/{id}. NO AI INVOLVED — no
  // executeAIAction, no aiActionExecutor. Just a read of the template,
  // a deep clone, and a single set() at /tournaments/{newId}. Then
  // navigate the director straight to the new tournament's admin page
  // so they can configure flights, tee times, brackets, calcutta etc.
  // exactly the way the demo button used to work before the AI was added.
  const setUpManually = async (kind) => {
    const templatePath = MANUAL_TEMPLATE_PATHS[kind]
    if (!templatePath) {
      setErr(`Unknown demo kind: ${kind}`)
      return
    }
    setBusy(true)
    setErr('')
    try {
      const newId = await copyTemplateToTournament(templatePath, {
        name: MANUAL_DEMO_NAMES[kind] || 'Tournament Demo',
        date: new Date().toISOString().slice(0, 10),
      })
      setOpen(false)
      setActive(null)
      navigate(`/admin/t/${newId}`)
    } catch (e) {
      setErr(e?.message || String(e))
    } finally {
      setBusy(false)
    }
  }

  // "Set Up with AI" — the AI already knows exactly what to do here (copy the
  // saved PBCC template), so just RUN it on click. No closing the modal, no
  // sending the director off to the floating ✨ circle to type. We invoke the
  // real AI executor's COPY_TOURNAMENT action against the saved template, then
  // drop the director straight into the freshly-built tournament — same landing
  // as "Set Up Manually", but through the AI path (deep-copy + verification).
  const setUpWithAI = async (kind, size = 96) => {
    // Route through buildDemoTournament — the SAME shared builder the floating
    // AI circle uses — so the tab produces the identical result: 4-Ball clones
    // the size-correct good template (96→ratliff, 104→ratliff13) with the
    // 3-day bracket + 8am/1pm flip tees; strokePlay clones its template.
    const mode = kind === 'full' ? 'fullTournament'
      : kind === 'strokePlay' ? 'strokePlay'
      : null
    if (!mode) {
      setErr(`Unknown demo kind: ${kind}`)
      return
    }
    setBusy(true)
    setErr('')
    try {
      const result = await buildDemoTournament(db, { mode, size })
      if (!result?.success) {
        setErr(result?.message || 'AI setup failed.')
        return
      }
      setOpen(false)
      setActive(null)
      navigate(`/admin/t/${result.tournamentId}`)
    } catch (e) {
      setErr(e?.message || String(e))
    } finally {
      setBusy(false)
    }
  }

  return (
    <>
      <button
        className={buttonClass}
        onClick={() => { setActive(null); setErr(''); setOpen(true) }}
        title="Launch a demo tournament">
        ▶ Try a Demo
      </button>
      {open ? (
        <DemoModal
          onClose={() => { setActive(null); setErr(''); setOpen(false) }}
          active={active}
          launch={launch}
          busy={busy}
          err={err}
          fullSize={fullSize}
          setFullSize={setFullSize}
          onBack={() => setActive(null)}
          onBuild={buildTeamsOnly}
          onSetupWithAI={setUpWithAI}
          onSetupManually={setUpManually}
        />
      ) : null}
    </>
  )
}

function DemoModal({ onClose, active, launch, busy, err, fullSize, setFullSize, onBack, onBuild, onSetupWithAI, onSetupManually }) {
  return (
    <div
      className="fixed inset-0 flex items-start justify-center p-4 z-50 overflow-y-auto"
      style={{ backgroundColor: 'rgba(var(--ge-primary-rgb),0.78)' }}
      onClick={onClose}>
      <div
        className="rounded-2xl max-w-2xl w-full p-6 my-8"
        style={{
          backgroundColor: '#FFFFFF',
          border: '1px solid rgba(var(--ge-primary-rgb), 0.30)',
          boxShadow: '0 24px 60px rgba(0, 0, 0, 0.45)',
        }}
        onClick={(e) => e.stopPropagation()}>
        <div className="flex justify-between items-start gap-3">
          <div>
            <span className="pbcc-gold-rule" aria-hidden="true" />
            <h3 className="pbcc-h2 text-2xl mt-1">Try a Demo</h3>
            <p className="text-sm text-gray-600 mt-1">
              Scan QR codes on two phones to test any format instantly.
              Auto-cleans after 30 minutes.
            </p>
          </div>
          <button
            className="text-2xl leading-none transition"
            style={{ color: 'var(--ge-primary)' }}
            onMouseEnter={(e) => { e.currentTarget.style.color = 'var(--ge-primary)' }}
            onMouseLeave={(e) => { e.currentTarget.style.color = 'var(--ge-primary)' }}
            onClick={onClose}
            aria-label="Close">
            ×
          </button>
        </div>

        {/* Strict either/or: a single ternary picks between the demo-cards
            picker and the active panel. Only one branch is mounted at
            any time — the other branch's DOM doesn't exist, so it cannot
            visually overlap. */}
        {active ? (
          <ActivePanel
            active={active}
            onBack={onBack}
            busy={busy}
            err={err}
            onBuild={onBuild}
            onSetupWithAI={onSetupWithAI}
            onSetupManually={onSetupManually}
          />
        ) : (
          <DemoCards launch={launch} busy={busy} err={err} fullSize={fullSize} setFullSize={setFullSize} />
        )}
      </div>
    </div>
  )
}

// Picker view — 4 demo choice cards + (when set) any launch error.
// Mounted only when no demo is active.
function DemoCards({ launch, busy, err, fullSize, setFullSize }) {
  return (
    <>
      <div className="grid sm:grid-cols-2 gap-3 mt-5">
        {BUTTONS.map((b) => {
          const isFull = b.key === 'fullTournament'
          // Full Tournament card shows a live team count reflecting the toggle.
          const desc = isFull
            ? `Complete ${fullSize}-team 4-Ball Championship with all features`
            : b.desc
          const card = (
            <button
              onClick={() => b.comingSoon ? null : launch(b.key, isFull ? fullSize : undefined)}
              disabled={busy || b.comingSoon}
              title={b.comingSoon ? 'Coming soon' : `Launch ${b.label}`}
              className="w-full text-left rounded-xl px-4 py-3.5 transition active:scale-[0.99] disabled:opacity-60 disabled:cursor-not-allowed"
              style={b.comingSoon ? {
                background: 'rgba(var(--ge-primary-rgb), 0.10)',
                color: '#6B7280',
                border: '1.5px solid rgba(var(--ge-primary-rgb), 0.15)',
              } : {
                background: 'var(--ge-primary)',
                color: '#FFFFFF',
                border: '1.5px solid transparent',
                outline: '1.5px solid transparent',
                outlineOffset: '-2px',
                boxShadow: '0 6px 18px rgba(0, 0, 0, 0.18)',
              }}
              onMouseEnter={(e) => {
                if (b.comingSoon) return
                e.currentTarget.style.outlineColor = 'var(--ge-primary)'
                e.currentTarget.style.background = '#061A0F'
              }}
              onMouseLeave={(e) => {
                if (b.comingSoon) return
                e.currentTarget.style.outlineColor = 'transparent'
                e.currentTarget.style.background = 'var(--ge-primary)'
              }}>
              <div className="flex items-center justify-between gap-2">
                <span className="text-2xl">{b.icon}</span>
                {b.comingSoon ? (
                  <span className="text-[10px] uppercase tracking-widest font-extrabold rounded-full px-2 py-0.5"
                        style={{ backgroundColor: 'rgba(var(--ge-primary-rgb), 0.20)', color: 'var(--ge-primary)' }}>
                    Coming Soon
                  </span>
                ) : (
                  <span style={{ color: 'var(--ge-primary)' }} className="text-[10px] uppercase tracking-widest font-extrabold">
                    Launch
                  </span>
                )}
              </div>
              <div className="font-extrabold text-sm leading-tight mt-2" style={{ letterSpacing: '0.02em' }}>{b.label}</div>
              {desc ? (
                <div className={`text-xs leading-snug mt-1.5 ${b.comingSoon ? 'text-gray-500' : 'text-white/80'}`}>
                  {desc}
                </div>
              ) : null}
            </button>
          )
          // The field-size toggle lives OUTSIDE the launch button (buttons
          // can't nest) so tapping 96/104 only sets the size — you launch by
          // tapping the card itself.
          if (!isFull) return <div key={b.key}>{card}</div>
          return (
            <div key={b.key} className="flex flex-col gap-2">
              {card}
              <FieldSizeToggle size={fullSize} setSize={setFullSize} busy={busy} />
            </div>
          )
        })}
      </div>
      {err ? <p className="text-sm font-bold mt-3" style={{ color: '#B91C1C' }}>{err}</p> : null}
    </>
  )
}

// Segmented 96 / 104 control for the Full Tournament Demo field size.
// Tapping a segment only updates the pending size; the card's own tap
// launches the demo with whatever is selected. Default 96.
function FieldSizeToggle({ size, setSize, busy }) {
  // Any field size, 1-13 flights of 8. size is teams (flights × 8).
  const flights = Math.max(1, Math.min(13, Math.round((Number(size) || 96) / 8)))
  return (
    <div>
      <p className="text-[10px] uppercase tracking-widest font-extrabold mb-1" style={{ color: 'var(--ge-primary)' }}>
        Field size
      </p>
      <select
        value={flights}
        disabled={busy}
        onChange={(e) => setSize(Number(e.target.value) * 8)}
        className="input"
        style={{ borderColor: 'rgba(var(--ge-primary-rgb),0.5)' }}>
        {Array.from({ length: 13 }, (_, i) => i + 1).map((n) => (
          <option key={n} value={n}>{n} flight{n === 1 ? '' : 's'} — {n * 8} teams</option>
        ))}
      </select>
    </div>
  )
}

// Active view — picks between the full-tournament roster, the
// stroke-play roster, and the QR-code panel for the other demos.
// Mounted only when a demo is active. The setup-action buttons are
// only relevant for the template-backed demos (full + strokePlay).
function ActivePanel({ active, onBack, busy, err, onBuild, onSetupWithAI, onSetupManually }) {
  // Teams-only scramble: TEAM LIST → BUILD → QR (the real program sequence).
  if (active.stage) {
    return <ScrambleActive active={active} onBack={onBack} busy={busy} err={err} onBuild={() => onBuild(active.key)} />
  }
  if (active.def?.kind === 'full') {
    return (
      <FullTournamentActive
        active={active} onBack={onBack} busy={busy} err={err}
        onSetupWithAI={() => onSetupWithAI('full', active?.teamRows?.length || active?.def?.teamRows?.length || 96)}
        onSetupManually={() => onSetupManually('full')}
      />
    )
  }
  if (active.def?.kind === 'strokePlay') {
    return (
      <StrokePlayActive
        active={active} onBack={onBack} busy={busy} err={err}
        onSetupWithAI={() => onSetupWithAI('strokePlay')}
        onSetupManually={() => onSetupManually('strokePlay')}
      />
    )
  }
  return <ActiveDemo active={active} onBack={onBack} />
}

// Two equal-weight CTAs at the bottom of each template-backed panel.
// "Set Up with AI" just dismisses the modal — the floating ✨ AI button
// remains visible on the dashboard, already in demo mode. "Set Up
// Manually" deep-copies the demo template into a new /tournaments/{id}
// and jumps the director straight to that tournament's admin page,
// matching the pre-AI demo behaviour.
function SetupActions({ busy, err, summary, onSetupWithAI, onSetupManually }) {
  // "Set Up with AI" is a two-step commit: the first click only arms the
  // confirm (shows the plain summary + Build/Cancel). NOTHING is created until
  // the green Build is clicked, which runs onSetupWithAI (the COPY_TOURNAMENT
  // build). Cancel disarms it — no tournament written.
  const [confirming, setConfirming] = useState(false)
  const baseStyle = {
    borderRadius: 10,
    padding: '12px 16px',
    fontWeight: 800,
    fontSize: 13,
    letterSpacing: '0.04em',
    textTransform: 'uppercase',
    cursor: busy ? 'not-allowed' : 'pointer',
    opacity: busy ? 0.6 : 1,
    boxShadow: '0 6px 18px rgba(0, 0, 0, 0.18)',
    border: '1.5px solid transparent',
  }

  if (confirming) {
    return (
      <div className="mt-4">
        <div
          className="rounded-lg p-3"
          style={{ background: '#FFFFFF', border: '1px solid rgba(var(--ge-primary-rgb), 0.30)' }}>
          <p className="text-[11px] uppercase tracking-widest font-bold mb-1" style={{ color: 'var(--ge-primary)' }}>
            Confirm — this will create a new tournament
          </p>
          <p className="text-sm font-extrabold" style={{ color: 'var(--ge-primary)' }}>{summary}</p>
        </div>
        <div className="grid sm:grid-cols-2 gap-3 mt-3">
          <button
            type="button"
            onClick={onSetupWithAI}
            disabled={busy}
            style={{ ...baseStyle, background: '#16A34A', color: '#FFFFFF' }}>
            {busy ? 'Building…' : '✓ Build'}
          </button>
          <button
            type="button"
            onClick={() => setConfirming(false)}
            disabled={busy}
            style={{ ...baseStyle, background: '#DC2626', color: '#FFFFFF' }}>
            ✗ Cancel
          </button>
        </div>
        {err ? <p className="text-sm font-bold mt-2" style={{ color: '#B91C1C' }}>{err}</p> : null}
      </div>
    )
  }

  return (
    <div className="mt-4">
      <div className="grid sm:grid-cols-2 gap-3">
        <button
          type="button"
          onClick={() => setConfirming(true)}
          disabled={busy}
          style={{ ...baseStyle, background: 'var(--ge-primary)', color: '#FFFFFF' }}>
          ✨ Set Up with AI
        </button>
        <button
          type="button"
          onClick={onSetupManually}
          disabled={busy}
          style={{ ...baseStyle, background: 'var(--ge-primary)', color: '#FFFFFF' }}>
          🛠 Set Up Manually
        </button>
      </div>
      <p className="text-[11px] mt-2 leading-snug" style={{ color: 'rgba(var(--ge-primary-rgb),0.65)' }}>
        AI: shows a quick summary, then builds from the saved PBCC template when
        you hit Build — and drops you into it. Manual: same template, but you
        configure flights, tee times, brackets, calcutta yourself.
      </p>
      {err ? <p className="text-sm font-bold mt-2" style={{ color: '#B91C1C' }}>{err}</p> : null}
    </div>
  )
}

// Stroke Play Demo writes nothing to /tournaments. Shows the 36-player
// roster sorted by handicap so the director can confirm the field, then
// directs them to ask the AI to spin up a live tournament from the
// saved /templates/demos/stroke-play-36 template.
function StrokePlayActive({ active, onBack, busy, err, onSetupWithAI, onSetupManually }) {
  const { def, playerRows } = active
  const rows = playerRows || def?.playerRows || []
  const playerCount = rows.length

  return (
    <div
      className="mt-5 rounded-xl p-4"
      style={{
        background: 'rgba(var(--ge-primary-rgb), 0.06)',
        border: '1.5px solid var(--ge-primary)',
        boxShadow: 'inset 0 0 0 1px rgba(var(--ge-primary-rgb), 0.18)',
      }}>
      {onBack ? (
        <button
          type="button"
          onClick={onBack}
          className="text-xs font-bold uppercase tracking-widest mb-2 transition"
          style={{ color: 'var(--ge-primary)' }}
          onMouseEnter={(e) => { e.currentTarget.style.color = 'var(--ge-primary)' }}
          onMouseLeave={(e) => { e.currentTarget.style.color = 'var(--ge-primary)' }}>
          ← Back to demos
        </button>
      ) : null}
      <div className="flex justify-between items-start gap-3 flex-wrap">
        <div>
          <p className="text-xs uppercase tracking-widest font-bold" style={{ color: 'var(--ge-primary)' }}>Demo Mode Active</p>
          <h4 className="font-extrabold text-base mt-0.5" style={{ color: 'var(--ge-primary)' }}>
            📊 Stroke Play Demo — {playerCount} Players Loaded
          </h4>
        </div>
      </div>

      <p className="text-sm mt-3" style={{ color: 'var(--ge-primary)' }}>
        {playerCount} players paired into 18 shotgun groups. <b>No tournament was created.</b>
      </p>

      <div
        className="mt-3 rounded-lg p-3"
        style={{ background: '#FFFFFF', border: '1px solid rgba(var(--ge-primary-rgb), 0.30)' }}>
        <p className="text-xs uppercase tracking-widest font-bold mb-2" style={{ color: 'var(--ge-primary)' }}>Next step</p>
        <p className="text-sm" style={{ color: 'var(--ge-primary)' }}>
          Close this modal, click the <b>🤖 AI Assistant</b> button, and say
          {' '}<b>"create a stroke play tournament"</b>. The AI will build it
          from the saved Stroke Play template — no questions asked.
        </p>
      </div>

      <div className="mt-4 rounded-lg p-3" style={{ background: '#FFFFFF', border: '1px solid rgba(var(--ge-primary-rgb), 0.30)' }}>
        <p className="text-xs uppercase tracking-widest font-bold mb-2" style={{ color: 'var(--ge-primary)' }}>
          {playerCount} Players Loaded (sorted by handicap)
        </p>
        <ol
          className="text-xs space-y-1 max-h-72 overflow-y-auto list-decimal list-inside"
          style={{ color: 'var(--ge-primary)' }}>
          {[...rows]
            .sort((a, b) => a[1] - b[1])
            .map(([name, hi], idx) => (
              <li key={`${name}-${idx}`} className="font-mono">
                <span className="font-bold">{name}</span>{' '}
                <span style={{ color: 'rgba(var(--ge-primary-rgb),0.55)' }}>· HI {hi}</span>
              </li>
            ))}
        </ol>
      </div>
      <SetupActions
        busy={busy} err={err}
        summary={SETUP_SUMMARY.strokePlay}
        onSetupWithAI={onSetupWithAI}
        onSetupManually={onSetupManually}
      />
    </div>
  )
}

// Full Tournament Demo writes nothing to /tournaments — no admin URL to
// link to. The button is a pure UI trigger that loads the 96-team roster
// preview and flips the dashboard AI Assistant into demo mode. The
// director closes the modal and asks the AI to "create a 4-ball" — the
// AI's demo-mode preamble locks it to COPY_TOURNAMENT from the saved
// PBCC template.
function FullTournamentActive({ active, onBack, busy, err, onSetupWithAI, onSetupManually }) {
  const { def, teamRows } = active
  const rows = teamRows || def?.teamRows || []
  const teamCount = rows.length

  return (
    <div
      className="mt-5 rounded-xl p-4"
      style={{
        background: 'rgba(var(--ge-primary-rgb), 0.06)',
        border: '1.5px solid var(--ge-primary)',
        boxShadow: 'inset 0 0 0 1px rgba(var(--ge-primary-rgb), 0.18)',
      }}>
      {onBack ? (
        <button
          type="button"
          onClick={onBack}
          className="text-xs font-bold uppercase tracking-widest mb-2 transition"
          style={{ color: 'var(--ge-primary)' }}
          onMouseEnter={(e) => { e.currentTarget.style.color = 'var(--ge-primary)' }}
          onMouseLeave={(e) => { e.currentTarget.style.color = 'var(--ge-primary)' }}>
          ← Back to demos
        </button>
      ) : null}
      <div className="flex justify-between items-start gap-3 flex-wrap">
        <div>
          <p className="text-xs uppercase tracking-widest font-bold" style={{ color: 'var(--ge-primary)' }}>Demo Mode Active</p>
          <h4 className="font-extrabold text-base mt-0.5" style={{ color: 'var(--ge-primary)' }}>🏆 Full Tournament Demo — {teamCount} Teams Loaded</h4>
        </div>
      </div>

      <p className="text-sm mt-3" style={{ color: 'var(--ge-primary)' }}>
        {teamCount} teams have been loaded into demo mode. <b>No tournament was created.</b>
      </p>

      <div
        className="mt-3 rounded-lg p-3"
        style={{ background: '#FFFFFF', border: '1px solid rgba(var(--ge-primary-rgb), 0.30)' }}>
        <p className="text-xs uppercase tracking-widest font-bold mb-2" style={{ color: 'var(--ge-primary)' }}>Next step</p>
        <p className="text-sm" style={{ color: 'var(--ge-primary)' }}>
          Close this modal, click the <b>🤖 AI Assistant</b> button, and say
          {' '}<b>"create a 4-Ball tournament"</b>. The AI will build it
          from the saved PBCC template — no questions asked.
        </p>
      </div>

      <div className="mt-4 rounded-lg p-3" style={{ background: '#FFFFFF', border: '1px solid rgba(var(--ge-primary-rgb), 0.30)' }}>
        <p className="text-xs uppercase tracking-widest font-bold mb-2" style={{ color: 'var(--ge-primary)' }}>
          {teamCount} Teams Loaded (sorted by qualifying score)
        </p>
        <ol
          className="text-xs space-y-1 max-h-72 overflow-y-auto list-decimal list-inside"
          style={{ color: 'var(--ge-primary)' }}>
          {[...rows]
            .sort((a, b) => a[1] - b[1])
            .map(([name, score], idx) => (
              <li key={`${name}-${idx}`} className="font-mono">
                <span className="font-bold">{name}</span>{' '}
                <span style={{ color: 'rgba(var(--ge-primary-rgb),0.55)' }}>· {score}</span>
              </li>
            ))}
        </ol>
      </div>
      <SetupActions
        busy={busy} err={err}
        summary={SETUP_SUMMARY.full}
        onSetupWithAI={onSetupWithAI}
        onSetupManually={onSetupManually}
      />
    </div>
  )
}

// Teams-only scramble panel — the real program sequence. Stage 'teamlist' shows
// the entered teams (no qualifying) + a BUILD button; stage 'qr' hands off to
// the QR panel AFTER the tournament + tee times are built.
function ScrambleActive({ active, onBack, busy, err, onBuild }) {
  const { def } = active
  // Field defs expose teams() (one flight); flight defs expose flights() (many).
  // Flatten either into one list for the roster, and note the flight count.
  const flightMap = def.flights ? def.flights() : null
  const flightCount = flightMap ? Object.keys(flightMap).length : 1
  const teams = flightMap
    ? Object.values(flightMap).reduce((acc, f) => ({ ...acc, ...(f.teams || {}) }), {})
    : (def.teams ? def.teams() : {})
  const rows = Object.entries(teams).map(([id, t]) => ({
    id, name: t.info?.name, players: t.info?.players || [], hole: t.info?.startingHole,
  }))
  return (
    <div className="mt-5">
      <button
        type="button"
        onClick={onBack}
        className="text-xs font-bold uppercase tracking-widest mb-2"
        style={{ color: 'var(--ge-primary)' }}>
        ← Back to demos
      </button>

      <div className="rounded-xl p-4" style={{ background: 'rgba(var(--ge-primary-rgb), 0.06)', border: '1.5px solid var(--ge-primary)' }}>
        <p className="text-xs uppercase tracking-widest font-bold" style={{ color: 'var(--ge-primary)' }}>Step 1 · Team List</p>
        <h4 className="font-extrabold text-base mt-0.5" style={{ color: 'var(--ge-primary)' }}>{def.label} · Teams Only</h4>
        <p className="text-sm mt-1" style={{ color: 'var(--ge-primary)' }}>
          No qualifying — {rows.length} teams entered directly.{' '}
          {flightCount > 1
            ? `${flightCount} flights — the leaderboard ranks within each flight.`
            : 'One field — everyone ranked against the whole field.'}
        </p>

        <div className="grid sm:grid-cols-2 gap-2 mt-3">
          {rows.map((r) => (
            <div key={r.id} className="rounded-lg px-3 py-2 flex items-center justify-between"
              style={{ background: '#FFFFFF', border: '1px solid rgba(var(--ge-primary-rgb), 0.25)' }}>
              <div className="min-w-0">
                <div className="text-sm font-bold truncate" style={{ color: 'var(--ge-primary)' }}>{r.name}</div>
                <div className="text-[10px] truncate" style={{ color: 'rgba(var(--ge-primary-rgb), 0.55)' }}>{r.players.join(' · ')}</div>
              </div>
              <div className="text-[10px] font-bold uppercase tracking-wider ml-2 whitespace-nowrap" style={{ color: 'rgba(var(--ge-primary-rgb), 0.70)' }}>Hole {r.hole}</div>
            </div>
          ))}
        </div>
      </div>

      <button
        type="button"
        onClick={onBuild}
        disabled={busy}
        className="w-full mt-4 rounded-xl font-extrabold uppercase tracking-widest disabled:opacity-60"
        style={{ background: 'var(--ge-primary)', color: '#FFFFFF', padding: '14px 16px', boxShadow: '0 6px 18px rgba(0,0,0,0.18)' }}>
        {busy ? 'Building with AI…' : '✨ Build with AI'}
      </button>
      <p className="text-[11px] mt-2 leading-snug" style={{ color: 'rgba(var(--ge-primary-rgb), 0.65)' }}>
        The AI builds the tournament and shotgun tee times, then drops you into the Tournament Center — TV control, live leaderboard, tee times, GPS, and the QR Codes tab — the same place the 4-Ball demo lands.
      </p>
      {err ? <p className="text-sm font-bold mt-2" style={{ color: '#B91C1C' }}>{err}</p> : null}
    </div>
  )
}

function ActiveDemo({ active, onBack }) {
  const { tournamentId, def } = active
  const origin = window.location.origin

  // Single-flight demos (scramble, individual) expose def.teams() and the
  // tournament is built with one flight keyed "flightA". Multi-flight demos
  // (4-ball match play) expose def.flights(), which returns a map of
  // {flightId: {info, teams}}. Flatten either shape into one list of
  // {flightId, teamId, t} so each QR points at the actual flight node the
  // team lives under.
  const flightMap = def.flights
    ? def.flights()
    : { flightA: { teams: def.teams ? def.teams() : {} } }
  const teamEntries = []
  for (const [flightId, flight] of Object.entries(flightMap)) {
    for (const [teamId, t] of Object.entries(flight.teams || {})) {
      teamEntries.push({ flightId, teamId, t })
    }
  }

  const teamUrl = (flightId, teamId) =>
    `${origin}/score?t=${tournamentId}&f=${flightId}&team=${teamId}`
  const leaderboardUrl = `${origin}/leaderboard?t=${tournamentId}`
  const mapUrl = `${origin}/map?t=${tournamentId}`

  return (
    <div
      className="mt-5 rounded-xl p-4"
      style={{
        background: 'rgba(var(--ge-primary-rgb), 0.06)',
        border: '1.5px solid var(--ge-primary)',
        boxShadow: 'inset 0 0 0 1px rgba(var(--ge-primary-rgb), 0.18)',
      }}>
      {onBack ? (
        <button
          type="button"
          onClick={onBack}
          className="text-xs font-bold uppercase tracking-widest mb-2 transition"
          style={{ color: 'var(--ge-primary)' }}
          onMouseEnter={(e) => { e.currentTarget.style.color = 'var(--ge-primary)' }}
          onMouseLeave={(e) => { e.currentTarget.style.color = 'var(--ge-primary)' }}>
          ← Back to demos
        </button>
      ) : null}
      <div className="flex justify-between items-start gap-3 flex-wrap">
        <div>
          <p className="text-xs uppercase tracking-widest font-bold" style={{ color: 'var(--ge-primary)' }}>Demo Active</p>
          <h4 className="font-extrabold text-base mt-0.5" style={{ color: 'var(--ge-primary)' }}>{def.label}</h4>
        </div>
        <p className="text-[11px] font-mono" style={{ color: 'rgba(var(--ge-primary-rgb), 0.60)' }}>{tournamentId}</p>
      </div>

      <p className="text-sm mt-3" style={{ color: 'var(--ge-primary)' }}>
        Scan a code on each phone, then start scoring. Live updates work both directions.
      </p>

      <div className="grid sm:grid-cols-2 gap-3 mt-3">
        {teamEntries.map(({ flightId, teamId, t }) => (
          <div
            key={`${flightId}-${teamId}`}
            className="rounded-xl p-3 text-center"
            style={{ background: '#FFFFFF', border: '1px solid rgba(var(--ge-primary-rgb), 0.30)' }}>
            <div className="text-xs uppercase tracking-wider font-bold" style={{ color: 'var(--ge-primary)' }}>{t.info?.name}</div>
            <div className="text-[10px] mt-0.5" style={{ color: 'rgba(var(--ge-primary-rgb), 0.55)' }}>{(t.info?.players || []).join(' · ')}</div>
            <div className="my-2 flex justify-center">
              <QRCodeCanvas value={teamUrl(flightId, teamId)} size={130} includeMargin />
            </div>
            <div className="text-[9px] break-all" style={{ color: 'rgba(var(--ge-primary-rgb), 0.45)' }}>{teamUrl(flightId, teamId)}</div>
          </div>
        ))}
      </div>

      <DemoLinks
        adminUrl={`${origin}/admin/t/${tournamentId}`}
        leaderboardUrl={leaderboardUrl}
        mapUrl={mapUrl}
      />
    </div>
  )
}

// Three uniform dark-green / gold buttons that replace the raw URL text.
// Gold outline on hover matches the access-code page + Admin sidebar nav.
function DemoLinks({ adminUrl, leaderboardUrl, mapUrl }) {
  const linkStyle = {
    backgroundColor: 'var(--ge-primary)',
    color: '#FFFFFF',
    border: '1.5px solid var(--ge-primary)',
    outline: '1.5px solid transparent',
    outlineOffset: '-2px',
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.18)',
  }
  const onEnter = (e) => { e.currentTarget.style.outlineColor = '#FFFFFF'; e.currentTarget.style.background = '#061A0F' }
  const onLeave = (e) => { e.currentTarget.style.outlineColor = 'transparent'; e.currentTarget.style.background = 'var(--ge-primary)' }
  const cls = 'rounded-lg px-3 py-2.5 text-center text-xs font-extrabold uppercase tracking-wider transition active:scale-[0.98]'
  return (
    <div className="mt-3 grid sm:grid-cols-3 gap-2">
      <a href={adminUrl} className={cls} style={linkStyle} onMouseEnter={onEnter} onMouseLeave={onLeave}>🏌️ Open Tournament</a>
      <a href={leaderboardUrl} target="_blank" rel="noreferrer" className={cls} style={linkStyle} onMouseEnter={onEnter} onMouseLeave={onLeave}>🏆 Live Leaderboard</a>
      <a href={mapUrl} target="_blank" rel="noreferrer" className={cls} style={linkStyle} onMouseEnter={onEnter} onMouseLeave={onLeave}>📡 Command Center</a>
    </div>
  )
}
